import { SimClockFollower, type Clock, type Instant, type Micros, type TimerHandle } from "@beacon/clock";

/** A source of "now" in milliseconds for record-relative age display. */
export interface ClockSource {
  nowMs(): number;
}

/** Production / non-simulation: real wall-clock time. */
export const systemClockSource: ClockSource = {
  // wallclock-allow: THE display clock in production (apps/webapp excluded from gate-30).
  nowMs: () => Date.now(),
};

interface SimClockResponse {
  now: number; // sim micros since epoch
  rate: number; // sim-µs per mono-µs
}

export interface RelaySimClockOptions {
  baseUrl: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Monotonic micros source; injectable for tests. */
  monotonic?: () => number;
  /** 10-bit clock id (unused for now()/age; only matters if we ever mint TIDs). */
  clkid?: number;
}

/**
 * A sim-aware clock that follows the relay's SimFollower (BEACON_MODE=simulation).
 *
 * In simulation, records are stamped (`createdAt`) on a virtual clock anchored at
 * Instant 0 = the Unix epoch, so comparing them against wall time reads "494155 h
 * ago". This follows the relay's `GET /sim/clock`: each `sync()` re-anchors a local
 * {@link SimClockFollower} against the browser's own monotonic clock and adopts the
 * reported rate, so `nowMs()` extrapolates sim time smoothly between polls.
 *
 * Until the first successful sync (and if a sync fails) it falls back to wall time
 * so nothing blocks while the relay is unreachable; the caller re-syncs on a timer.
 *
 * It is also a full {@link Clock}: BeaconClient reads now()/tid() to stamp record
 * `createdAt` (and mint TIDs) on sim-time, so a human's own messages sort in the
 * SAME domain as agent broadcasts (~1970 in BEACON_MODE=simulation) — otherwise a
 * wall-clock createdAt lands ~56 years in the future and the Talk feed jumps.
 * Timers (after/at), however, run on WALL time: the webapp is a passive follower,
 * so a reconnect backoff must elapse even when the sim is paused (rate 0 would make
 * a sim-time delay never fire). Only timestamps are sim-time; scheduling is real.
 */
export class RelaySimClock implements ClockSource, Clock {
  readonly #follower: SimClockFollower;
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #mono: () => number;
  #generation = 0;
  #anchored = false;

  constructor(opts: RelaySimClockOptions) {
    this.#baseUrl = opts.baseUrl;
    this.#fetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#mono =
      opts.monotonic ??
      // wallclock-allow: monotonic extrapolator read, not a time-of-day read (apps/webapp excluded from gate-30).
      (() => Math.round(performance.now() * 1000));
    this.#follower = new SimClockFollower({ clkid: opts.clkid ?? 0, monotonic: this.#mono });
  }

  /** Fetch the relay's current sim-now and re-anchor the local follower. Throws on
   *  a non-200 response (production 404s the route) so the caller can fall back. */
  async sync(): Promise<void> {
    const res = await this.#fetch(`${this.#baseUrl}/sim/clock`);
    if (!res.ok) throw new Error(`sim clock sync failed: ${res.status}`);
    const { now, rate } = (await res.json()) as SimClockResponse;
    this.#follower.applyClockState({
      anchorSim: now,
      anchorMono: this.#mono(),
      rate,
      generation: ++this.#generation,
    });
    this.#anchored = true;
  }

  nowMs(): number {
    // wallclock-allow: pre-sync fallback so an unreachable relay never blocks the UI.
    if (!this.#anchored) return Date.now();
    return Math.trunc(this.#follower.now() / 1000);
  }

  // --- Clock (envelope timestamps + TIDs on sim-time) ----------------------

  now(): Instant {
    // wallclock-allow: pre-sync fallback so an unsynced clock never stamps 1970.
    if (!this.#anchored) return Date.now() * 1000;
    return this.#follower.now();
  }

  tid(): string {
    return this.#follower.tid(); // sim-time minter (used only after sync in practice)
  }

  after(d: Micros, cb: () => void): TimerHandle {
    // wallclock-allow: real-time timer (apps/webapp excluded from gate-30). NOT the
    // sim follower's heap — see the class doc: timers must fire on wall time so a
    // paused sim (rate 0) can't stall a reconnect backoff.
    const id = setTimeout(cb, d / 1000);
    return { cancel: () => clearTimeout(id) };
  }

  at(t: Instant, cb: () => void): TimerHandle {
    return this.after(Math.max(0, t - this.now()), cb);
  }
}
